import type { FaqItem } from './services';

export type KitchenCityContent = {
  citySlug: string;
  metaTitle: string;
  metaDescription: string;
  eyebrow: string;
  heroTitle: string;
  heroLead: string;
  introTitle: string;
  introParagraphs: string[];
  whyTitle: string;
  whyItems: string[];
  costLead: string;
  servicesTitle: string;
  servicesIntro: string;
  serviceItems: { title: string; body: string }[];
  timelineLead: string;
  processTitle: string;
  processSteps: { label: string; body: string }[];
  materialsTitle: string;
  materialsIntro: string;
  materials: string[];
  considerationsTitle: string;
  considerations: { title: string; body: string }[];
  areasTitle: string;
  areasIntro: string;
  areas: string[];
  nearbyTitle: string;
  nearby: { label: string; href: string }[];
  faqs: FaqItem[];
  ctaTitle: string;
  ctaLead: string;
};

/**
 * Unique kitchen × city landing content.
 * Shared Texas cost/timeline/material tables come from KITCHEN_PAGE on the template.
 */
export const KITCHEN_CITY_PAGES: Record<string, KitchenCityContent> = {
  austin: {
    citySlug: 'austin',
    metaTitle: 'Kitchen Remodeling in Austin, TX | 2026 Cost Guide | Epic Home Pros',
    metaDescription:
      'Austin kitchen remodeling with a 2026 Texas cost guide, timelines, and free on-site estimates across the Austin metro.',
    eyebrow: 'Austin, TX Kitchen Remodeling',
    heroTitle: 'Kitchen Remodeling in Austin, TX',
    heroLead:
      'From minor refreshes to structural guts - plan with clear Texas cost tiers, then get a free on-site estimate for your Austin-area home.',
    introTitle: 'Kitchen remodeling for Central Texas homes',
    introParagraphs: [
      'Austin kitchens range from compact mid-century footprints in older neighborhoods to open-concept plans in newer suburban builds. That mix changes what a remodel can realistically include - storage, islands, plumbing moves, and how the kitchen connects to living space.',
      'Homeowners remodel to fix worn surfaces, add an island, update appliances, open a closed wall, or simply make daily cooking less frustrating. Scope can stay in the same footprint or grow into a larger transformation.',
      'Published 2026 Texas ranges help frame early budget talks. Your Austin written estimate after an on-site visit is the number that applies to your house.',
      'Epic Home Pros helps Austin-area homeowners start with one request. We follow up, schedule an on-site visit, and provide a written estimate. You decide whether to move forward. Free estimates. No obligation.',
    ],
    whyTitle: 'Why Austin homeowners remodel kitchens',
    whyItems: [
      'Worn cabinets and counters that no longer match how the household cooks',
      'Need for better storage in tighter Central Texas footprints',
      'Desire for an island or open connection to living areas',
      'Appliance upgrades that require new clearances or electrical',
      'Preparing a home for resale or longer-term living',
    ],
    costLead:
      'Austin projects still land inside statewide planning tiers. Compact older kitchens may lean cosmetic or mid-range, while open-concept suburban guts sit higher when plumbing and openings change. Use the guide below as context - not a quote.',
    servicesTitle: 'Kitchen remodeling services in Austin',
    servicesIntro:
      'Projects commonly include the categories below. Your written estimate will define what is in scope for your house.',
    serviceItems: [
      {
        title: 'Cabinets and storage',
        body: 'New cabinets, refacing when boxes are sound, pantry builds, and drawer upgrades that fit how you cook.',
      },
      {
        title: 'Counters and backsplash',
        body: 'Quartz, stone, and solid-surface options with backsplash details that finish the wall line.',
      },
      {
        title: 'Layout and islands',
        body: 'Same-footprint updates or layout changes such as islands, widened openings, and work-zone improvements when the house allows.',
      },
      {
        title: 'Lighting, plumbing, and appliances',
        body: 'Task and ambient lighting, sink and faucet updates, and appliance planning before cabinets are finalized.',
      },
    ],
    timelineLead:
      'Cabinet and stone lead times affect Austin schedules the same way they do statewide. Cosmetic work can finish in a few weeks; structural openings stretch into multi-month windows.',
    processTitle: 'How a kitchen quote works in Austin',
    processSteps: [
      {
        label: '1. Request',
        body: 'Share your Austin-area city, refresh vs full remodel goals, and photos if you have them.',
      },
      {
        label: '2. On-site visit',
        body: 'We schedule a visit to measure the kitchen, talk layout, and review existing conditions.',
      },
      {
        label: '3. Written estimate',
        body: 'You receive a written estimate for your house. You decide whether to move forward.',
      },
    ],
    materialsTitle: 'Materials that fit Austin kitchens',
    materialsIntro:
      'Material choices drive both look and budget. Bring preferences to the visit so options can be priced accurately.',
    materials: [
      'Cabinet construction level - stock, semi-custom, or custom paths',
      'Countertop material and edge profiles',
      'Flooring transitions into adjoining rooms',
      'Lighting layers for counters, islands, and evenings',
      'Appliance sizes and fuel type before cabinetry is ordered',
    ],
    considerationsTitle: 'What Austin homeowners should decide early',
    considerations: [
      {
        title: 'Footprint vs open plan',
        body: 'Decide whether the existing wall plan still works or whether an opening to living space is worth the structural cost.',
      },
      {
        title: 'Storage in compact rooms',
        body: 'Older Central Texas kitchens often gain more from drawers and pantry builds than from forcing a large island.',
      },
      {
        title: 'Outdoor connections',
        body: 'If you cook for patio entertaining, plan traffic from kitchen to outdoor living before locking island size.',
      },
    ],
    areasTitle: 'Austin service area context',
    areasIntro:
      'We serve kitchen remodeling requests across the Austin metro and nearby communities. Coverage is confirmed from your quote address.',
    areas: [
      'Austin proper and central neighborhoods with varied housing ages',
      'Suburban metros where open-concept kitchens are common',
      'Homes where plumbing and electrical access should be checked on site before locking layout changes',
    ],
    nearbyTitle: 'Nearby areas',
    nearby: [
      { label: 'Dallas kitchen remodeling', href: '/services/kitchen-remodeling/dallas' },
      { label: 'Houston kitchen remodeling', href: '/services/kitchen-remodeling/houston' },
      { label: 'San Antonio kitchen remodeling', href: '/services/kitchen-remodeling/san-antonio' },
      { label: 'Austin remodeling overview', href: '/locations/austin' },
      { label: 'Service Areas', href: '/service-areas' },
    ],
    faqs: [
      {
        question: 'How much does a kitchen remodel cost in Austin?',
        answer:
          'Use the 2026 Texas planning tiers: about $10,000-$25,000 minor refresh, $25,000-$45,000 cosmetic, $45,000-$90,000 mid-range, $90,000-$150,000 high-end, and $150,000+ for luxury or structural work. Your Austin-area written estimate prices your house.',
      },
      {
        question: 'How long does an Austin kitchen remodel take?',
        answer:
          'Approximate windows match statewide guidance: a few weeks for cosmetic work, about 6-10 weeks for many mid-range projects, and 12+ weeks when structural or major material lead times apply.',
      },
      {
        question: 'Do Austin kitchen remodels usually need permits?',
        answer:
          'Like-for-like cosmetic updates in the same layout often need fewer permits than projects that move plumbing, gas, electrical, or walls. Requirements vary. An on-site visit is the right time to discuss what your scope may trigger.',
      },
      {
        question: 'Can you remodel a small Austin kitchen without expanding?',
        answer:
          'Yes. Many smaller kitchens improve most from storage, lighting, continuous flooring, and right-sized appliances rather than forcing a large island.',
      },
      {
        question: 'Is the Austin kitchen estimate free?',
        answer: 'Yes. Free on-site estimate with no obligation to move forward.',
      },
    ],
    ctaTitle: 'Ready for an Austin kitchen quote?',
    ctaLead: 'Free on-site estimate. No obligation.',
  },
  dallas: {
    citySlug: 'dallas',
    metaTitle: 'Kitchen Remodeling in Dallas, TX | 2026 Cost Guide | Epic Home Pros',
    metaDescription:
      'Dallas and DFW kitchen remodeling with a 2026 Texas cost guide, timelines, and free on-site estimates.',
    eyebrow: 'Dallas, TX Kitchen Remodeling',
    heroTitle: 'Kitchen Remodeling in Dallas, TX',
    heroLead:
      'DFW kitchen remodeling quotes with free on-site estimates - plan cost tiers first, then price your house.',
    introTitle: 'Kitchen remodeling across Dallas-Fort Worth',
    introParagraphs: [
      'Dallas-area homes span older in-town layouts and larger suburban kitchens. Remodels often update cabinets, counters, and lighting while confirming what plumbing and electrical changes the house can support.',
      'Whether you want a cosmetic refresh or a fuller rebuild, the best next step is an on-site look at measurements and existing conditions - not a phone-only guess.',
      '2026 Texas planning ranges give early budget context. Cabinets, counters, and layout changes are what usually move Dallas projects between tiers.',
      'Epic Home Pros helps Dallas and DFW homeowners request one quote, schedule a visit, and review a written estimate. Free estimates. No obligation.',
    ],
    whyTitle: 'Why Dallas homeowners remodel kitchens',
    whyItems: [
      'Updating dated cabinets and finishes in established DFW neighborhoods',
      'Improving workflow in galley or closed kitchens',
      'Adding islands or better seating for family gatherings',
      'Upgrading appliances and ventilation',
      'Aligning the kitchen with how the rest of the home has been updated',
    ],
    costLead:
      'Larger DFW suburban kitchens often land mid-range to high-end when cabinets and appliances are fully replaced. Smaller in-town kitchens can stay in cosmetic or mid-range tiers when the layout already works.',
    servicesTitle: 'Kitchen remodeling services in Dallas',
    servicesIntro: 'Common project scopes for Dallas-area kitchens include:',
    serviceItems: [
      {
        title: 'Full and cosmetic remodels',
        body: 'Surface refreshes in the same layout, or fuller replacements of cabinets, counters, and flooring.',
      },
      {
        title: 'Islands and open connections',
        body: 'Island additions and openings to living spaces when structure and budget allow.',
      },
      {
        title: 'Lighting and electrical updates',
        body: 'Recessed lighting, under-cabinet task light, and circuit needs for new appliances.',
      },
      {
        title: 'Plumbing and fixture upgrades',
        body: 'Sinks, faucets, and dishwasher connections - with relocation priced carefully when needed.',
      },
    ],
    timelineLead:
      'Custom cabinets and stone fabrication commonly set DFW schedules. Mid-range projects often fall near the 6-10 week planning window when materials arrive on time.',
    processTitle: 'How a kitchen quote works in Dallas',
    processSteps: [
      {
        label: '1. Request',
        body: 'Share your DFW city, project goals, and any must-haves such as an island or new appliances.',
      },
      {
        label: '2. On-site visit',
        body: 'We measure the kitchen and review layout, access, and existing conditions.',
      },
      {
        label: '3. Written estimate',
        body: 'You get a written estimate. You decide whether to move forward.',
      },
    ],
    materialsTitle: 'Materials and finish planning for Dallas',
    materialsIntro: 'Finish level and cabinet construction usually swing Dallas kitchen budgets more than square footage alone.',
    materials: [
      'Cabinet grade and hardware',
      'Quartz or stone counters',
      'Backsplash tile or slab details',
      'Flooring and transitions',
      'Appliance package sizing before cabinetry orders',
    ],
    considerationsTitle: 'What Dallas homeowners should decide early',
    considerations: [
      {
        title: 'Closed vs open kitchens',
        body: 'Many DFW homes still have closed walls to living rooms. Opening them can improve flow but adds structural cost.',
      },
      {
        title: 'Entertainment seating',
        body: 'Island seating is a frequent request. Confirm clearances for appliance doors before locking size.',
      },
      {
        title: 'Older mechanicals',
        body: 'In established neighborhoods, plumbing and electrical capacity should be checked before promising layout moves.',
      },
    ],
    areasTitle: 'Dallas-Fort Worth service area context',
    areasIntro:
      'We serve kitchen remodeling requests across Dallas and surrounding DFW communities when coverage allows.',
    areas: [
      'Dallas proper and nearby suburbs',
      'Fort Worth and Arlington coverage confirmed by quote address',
      'Homes where older plumbing or electrical may affect remodel scope',
    ],
    nearbyTitle: 'Nearby areas',
    nearby: [
      { label: 'Fort Worth remodeling', href: '/locations/fort-worth' },
      { label: 'Arlington remodeling', href: '/locations/arlington' },
      { label: 'Austin kitchen remodeling', href: '/services/kitchen-remodeling/austin' },
      { label: 'Dallas remodeling overview', href: '/locations/dallas' },
      { label: 'Service Areas', href: '/service-areas' },
    ],
    faqs: [
      {
        question: 'Do you cover Fort Worth and Arlington for kitchens?',
        answer:
          'Yes - Fort Worth and Arlington are part of our DFW service area when coverage allows. Submit a quote with your address and we will confirm.',
      },
      {
        question: 'How much does a Dallas kitchen remodel cost?',
        answer:
          'Use the 2026 Texas planning tiers on this page: roughly $10k-$25k minor, $25k-$45k cosmetic, $45k-$90k mid-range, $90k-$150k high-end, and $150k+ structural. Your written estimate is house-specific.',
      },
      {
        question: 'How long does a Dallas kitchen remodel take?',
        answer:
          'Cosmetic work can land in a few weeks. Many mid-range projects plan around 6-10 weeks. Structural or custom material jobs often need 12 weeks or more.',
      },
      {
        question: 'Should I replace cabinets or reface them?',
        answer:
          'Refacing can work when boxes are sound and the layout still fits. Full replacement is better when sizes, storage, or configuration need to change.',
      },
      {
        question: 'Is the estimate free?',
        answer: 'Yes. Free on-site estimate with no obligation.',
      },
    ],
    ctaTitle: 'Ready for a Dallas kitchen quote?',
    ctaLead: 'Free on-site estimate. No obligation.',
  },
  houston: {
    citySlug: 'houston',
    metaTitle: 'Kitchen Remodeling in Houston, TX | 2026 Cost Guide | Epic Home Pros',
    metaDescription:
      'Houston kitchen remodeling with a 2026 Texas cost guide, humidity-aware materials notes, timelines, and free on-site estimates.',
    eyebrow: 'Houston, TX Kitchen Remodeling',
    heroTitle: 'Kitchen Remodeling in Houston, TX',
    heroLead:
      'Greater Houston kitchen remodeling with free on-site estimates - plan cost, materials, and timeline before you commit.',
    introTitle: 'Kitchen remodeling for Greater Houston homes',
    introParagraphs: [
      'Houston-area kitchens benefit from finish choices that handle humidity and busy household use. Remodels range from cosmetic updates to full cabinet and layout changes.',
      'Open plans and connections to outdoor living are common conversation points. An on-site visit keeps ventilation, flooring transitions, and appliance clearances grounded in the house.',
      'Use 2026 Texas planning ranges for early budgeting. Durability and ventilation choices often matter as much as style in Greater Houston homes.',
      'Request a quote for your Houston-area address. We follow up, schedule a visit, and provide a written estimate. Free estimates. No obligation.',
    ],
    whyTitle: 'Why Houston homeowners remodel kitchens',
    whyItems: [
      'Updating finishes after years of Gulf Coast humidity and daily use',
      'Improving storage and counter space for larger households',
      'Opening the kitchen to living or outdoor areas',
      'Modernizing lighting and appliances',
      'Preparing for longer-term living or resale',
    ],
    costLead:
      'Humidity, ventilation, and larger open plans can push Houston kitchens toward mid-range and high-end material packages even when the footprint stays the same. Structural openings and outdoor connections add cost when included.',
    servicesTitle: 'Kitchen remodeling services in Houston',
    servicesIntro: 'Typical Houston-area kitchen scopes include:',
    serviceItems: [
      {
        title: 'Cabinets, counters, and backsplash',
        body: 'Full replacements or focused refreshes depending on budget and layout goals.',
      },
      {
        title: 'Flooring and transitions',
        body: 'Durable flooring choices and clean transitions into adjoining rooms.',
      },
      {
        title: 'Lighting and ventilation',
        body: 'Task lighting and range ventilation planned with appliance selections.',
      },
      {
        title: 'Layout improvements',
        body: 'Islands, pantry builds, and openings when structure and budget support them.',
      },
    ],
    timelineLead:
      'Appliance and cabinet lead times still set most calendars. Add time when outdoor kitchen connections or structural openings are part of the same project.',
    processTitle: 'How a kitchen quote works in Houston',
    processSteps: [
      {
        label: '1. Request',
        body: 'Share your city (Houston or nearby suburb), goals, and photos if available.',
      },
      {
        label: '2. On-site visit',
        body: 'We measure and review layout, utilities, and finish preferences.',
      },
      {
        label: '3. Written estimate',
        body: 'You receive a written estimate and decide next steps.',
      },
    ],
    materialsTitle: 'Materials that hold up in Houston homes',
    materialsIntro:
      'Durability and maintenance matter in humid climates. Bring must-haves and nice-to-haves to the visit.',
    materials: [
      'Cabinet finishes suited to daily use',
      'Countertops that handle heat and cleaning',
      'Backsplash and flooring details',
      'Appliance and ventilation packages',
      'Lighting for large or open kitchens',
    ],
    considerationsTitle: 'What Houston homeowners should decide early',
    considerations: [
      {
        title: 'Humidity and finish durability',
        body: 'Choose cabinet and flooring products with maintenance in mind - not just showroom looks.',
      },
      {
        title: 'Ventilation',
        body: 'Range hood capacity and duct paths should be confirmed before cabinetry is ordered.',
      },
      {
        title: 'Outdoor living links',
        body: 'If the kitchen feeds a patio or outdoor kitchen, plan traffic and flooring transitions together.',
      },
    ],
    areasTitle: 'Houston service area context',
    areasIntro:
      'We serve kitchen remodeling requests across Houston and surrounding communities when coverage allows.',
    areas: [
      'Houston proper and nearby suburbs',
      'Areas such as Pasadena, Pearland, Sugar Land, and The Woodlands - confirm with a quote',
      'Homes where humidity and ventilation should inform finish choices',
    ],
    nearbyTitle: 'Nearby areas',
    nearby: [
      { label: 'The Woodlands remodeling', href: '/locations/the-woodlands' },
      { label: 'Sugar Land remodeling', href: '/locations/sugar-land' },
      { label: 'Pearland remodeling', href: '/locations/pearland' },
      { label: 'Houston remodeling overview', href: '/locations/houston' },
      { label: 'Service Areas', href: '/service-areas' },
    ],
    faqs: [
      {
        question: 'Do you serve Houston suburbs for kitchen remodels?',
        answer:
          'Yes when coverage allows - including communities such as Pasadena, Pearland, Sugar Land, and The Woodlands. Submit your city on the quote form to confirm.',
      },
      {
        question: 'How much does a Houston kitchen remodel cost?',
        answer:
          'Use the 2026 Texas planning tiers: about $10k-$25k minor refresh, $25k-$45k cosmetic, $45k-$90k mid-range, $90k-$150k high-end, and $150k+ structural. Your written estimate after an on-site visit is the number for your house.',
      },
      {
        question: 'How long does a Houston kitchen remodel take?',
        answer:
          'Expect roughly 3-6 weeks for cosmetic work, 6-10 weeks for many mid-range projects, and 12+ weeks when structural work or long lead materials are involved.',
      },
      {
        question: 'What should I prepare for the visit?',
        answer:
          'Project goals, preferred timing, and photos help. Note any appliance sizes you already chose.',
      },
      {
        question: 'Is the estimate free?',
        answer: 'Yes. Free on-site estimate with no obligation.',
      },
    ],
    ctaTitle: 'Ready for a Houston kitchen quote?',
    ctaLead: 'Free on-site estimate. No obligation.',
  },
  'san-antonio': {
    citySlug: 'san-antonio',
    metaTitle: 'Kitchen Remodeling in San Antonio, TX | 2026 Cost Guide | Epic Home Pros',
    metaDescription:
      'San Antonio kitchen remodeling with a 2026 Texas cost guide, timelines, materials notes, and free on-site estimates.',
    eyebrow: 'San Antonio, TX Kitchen Remodeling',
    heroTitle: 'Kitchen Remodeling in San Antonio, TX',
    heroLead:
      'San Antonio kitchen remodeling with free on-site estimates - practical cost tiers for how you cook and gather.',
    introTitle: 'Kitchen remodeling for San Antonio homes',
    introParagraphs: [
      'From established neighborhoods to newer suburban streets, San Antonio kitchens benefit from an on-site look at the existing footprint before finish selections lock in.',
      'Projects often focus on durable surfaces, better storage, and layouts that fit how the household cooks. Larger moves are possible when structure and budget support them.',
      '2026 Texas planning ranges help set expectations before the visit. Your written estimate prices the kitchen you actually have.',
      'Epic Home Pros helps San Antonio homeowners start with one request, an on-site visit, and a written estimate. Free estimates. No obligation.',
    ],
    whyTitle: 'Why San Antonio homeowners remodel kitchens',
    whyItems: [
      'Refreshing dated cabinets and counters',
      'Improving storage without wasting floor space',
      'Updating lighting for South Texas evenings',
      'Appliance and workflow upgrades',
      'Making the kitchen work better for family gatherings',
    ],
    costLead:
      'Many San Antonio projects start in cosmetic or mid-range tiers when the footprint stays put. High-end and structural numbers apply when custom cabinets, premium stone, or wall changes enter the plan.',
    servicesTitle: 'Kitchen remodeling services in San Antonio',
    servicesIntro: 'Common scopes include:',
    serviceItems: [
      {
        title: 'Cabinet and counter updates',
        body: 'Refreshes or full replacements sized to your footprint and budget.',
      },
      {
        title: 'Storage and pantry solutions',
        body: 'Drawers, pull-outs, and pantry builds that reduce counter clutter.',
      },
      {
        title: 'Lighting and fixtures',
        body: 'Task lighting, pendants, sinks, and faucet updates.',
      },
      {
        title: 'Layout refinements',
        body: 'Islands and openings when the house and budget allow.',
      },
    ],
    timelineLead:
      'Same statewide planning windows apply. Confirm cabinet and countertop order times early if you have a hard move-in or gathering date.',
    processTitle: 'How a kitchen quote works in San Antonio',
    processSteps: [
      {
        label: '1. Request',
        body: 'Share your city, remodel goals, and photos if you have them.',
      },
      {
        label: '2. On-site visit',
        body: 'We measure and discuss layout, materials, and priorities.',
      },
      {
        label: '3. Written estimate',
        body: 'You receive a written estimate and decide whether to move forward.',
      },
    ],
    materialsTitle: 'Materials and design choices',
    materialsIntro: 'Choose finishes for daily use and South Texas heat as much as for style.',
    materials: [
      'Cabinet styles and hardware',
      'Countertop materials',
      'Backsplash and flooring',
      'Lighting layers',
      'Appliance clearances',
    ],
    considerationsTitle: 'What San Antonio homeowners should decide early',
    considerations: [
      {
        title: 'Heat and daily use',
        body: 'Pick counters and cabinet finishes that handle busy cooking and South Texas temperatures.',
      },
      {
        title: 'Gathering space',
        body: 'If the kitchen is the social hub, plan seating and traffic before choosing an island size.',
      },
      {
        title: 'Keep or change the footprint',
        body: 'Many strong remodels stay in the existing layout and put budget into storage and finishes.',
      },
    ],
    areasTitle: 'San Antonio service area context',
    areasIntro: 'We serve kitchen remodeling requests across San Antonio and nearby communities when coverage allows.',
    areas: [
      'San Antonio proper and surrounding metros',
      'Homes where existing footprints drive realistic layouts',
      'Projects that start with storage and finish upgrades before structural moves',
    ],
    nearbyTitle: 'Nearby areas',
    nearby: [
      { label: 'Austin kitchen remodeling', href: '/services/kitchen-remodeling/austin' },
      { label: 'Houston kitchen remodeling', href: '/services/kitchen-remodeling/houston' },
      { label: 'Dallas kitchen remodeling', href: '/services/kitchen-remodeling/dallas' },
      { label: 'San Antonio remodeling overview', href: '/locations/san-antonio' },
      { label: 'Service Areas', href: '/service-areas' },
    ],
    faqs: [
      {
        question: 'Which kitchen services are available in San Antonio?',
        answer:
          'Cosmetic refreshes, full remodels, layout discussions, and related finish work - scoped after an on-site visit.',
      },
      {
        question: 'How much does a San Antonio kitchen remodel cost?',
        answer:
          'Use the 2026 Texas planning tiers: about $10k-$25k minor, $25k-$45k cosmetic, $45k-$90k mid-range, $90k-$150k high-end, and $150k+ structural. Your written estimate is specific to your home.',
      },
      {
        question: 'How long does a San Antonio kitchen remodel take?',
        answer:
          'Cosmetic projects often fall in the 3-6 week window. Mid-range work commonly plans around 6-10 weeks. Structural projects can need 12-20+ weeks.',
      },
      {
        question: 'Is there an obligation after the estimate?',
        answer: 'No. Estimates are free and you are not obligated to move forward.',
      },
      {
        question: 'How quickly will you respond?',
        answer: 'We aim to reply within one business day after your quote request.',
      },
    ],
    ctaTitle: 'Ready for a San Antonio kitchen quote?',
    ctaLead: 'Free on-site estimate. No obligation.',
  },
};

export function getKitchenCityContent(citySlug: string): KitchenCityContent | undefined {
  return KITCHEN_CITY_PAGES[citySlug];
}
